import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { useHistory } from 'react-router-dom'

import { Loader } from '@/components/loaders/Loader'
import { PageHeader } from '@/components/headers/PageHeader'
import { Card } from '@/components/cards/Card'
import * as redux from '@/reducers/commandCenter/commandCenter.redux'
import * as actions from '@/reducers/commandCenter/commandCenter.actions'
import { INBOX_FILTER_TYPE } from '@/reducers/savedFilters/savedFilters.constants'
import {
  setSavedFilterIdAndChangeSubscription,
  fetchAgentsWithCallStatus,
} from '@/reducers/commandCenter/commandCenter.actions'
import { SavedFilters } from '@/components/savedFilters/SavedFilters'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'

import { AgentCallStatusList } from './components/AgentCallStatusList'
import { Sentiment } from './components/Sentiment'
import { ListeningStatus } from './components/ListeningStatus'

import './CommandCenter.scss'

export const CommandCenterPage = ({ ldClient }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const {
    loading: commandCenterLoading,
    selectedSavedFilterId,
    filtersLoaded,
    socketOpen,
  } = useSelector((state) => state.commandCenter)
  const {
    organizationid: currentUserOrgId,
    own_organization_id: ownOrganizationId,
    hierarchy_manager: isHierarchyManager,
  } = useSelector((state) => state.currentUser)
  const childOrgSelected = isHierarchyManager && currentUserOrgId !== ownOrganizationId

  const handleSelectSavedFilter = async (value) => {
    if (!value) {
      await dispatch(setSavedFilterIdAndChangeSubscription(null))
    } else {
      await dispatch(setSavedFilterIdAndChangeSubscription(value))
    }
    await dispatch(fetchAgentsWithCallStatus())
  }

  useEffect(() => {
    const flags = ldClient ? ldClient.allFlags() : {}
    // If someone shouldn't have Command Center, we'll give them Call Explorer instead
    if (flags?.showCommandCenter === false) {
      history.push('/explorer')
    }
  })

  // Only while the roster is on screen does every agent's row need to stay
  // current. `ccSocketMiddleware` reads this to decide whether a lifecycle event
  // is worth an HTTP refresh; off this page the socket still runs for the
  // widget, and refreshing the whole team would be pure load.
  useEffect(() => {
    dispatch(redux.setRosterInUse(true))

    return () => dispatch(redux.setRosterInUse(false))
  }, [dispatch])

  // The socket is owned by CommandCenterSocketProvider for the whole session —
  // the live transcript and the Listen controls follow the supervisor off this
  // page, so the connection can't be torn down when the page unmounts.
  useEffect(() => {
    async function initialSetup() {
      if (!childOrgSelected) {
        await dispatch(actions.fetchInitialFilters(INBOX_FILTER_TYPE))
      }

      await dispatch(fetchAgentsWithCallStatus())
      dispatch(redux.setFiltersLoaded(true))
    }

    initialSetup()
  }, [])

  return (
    <>
      <PageHeader
        data-testid="command-center-page"
        title={
          <>
            Live Listen
            <InteractiveTooltip
              placement="bottom-start"
              content={
                socketOpen
                  ? 'Connected to live listen service'
                  : 'Disconnected from live listen service, refresh page or reach out to support if issue persists'
              }
            >
              <ListeningStatus
                data-testid="header-listening-status"
                status={socketOpen ? 'online' : 'offline'}
                absolute={false}
              />
            </InteractiveTooltip>
          </>
        }
        actions={
          // We hide filters for hierarchy managers impersonating child org.
          // We don't support having org-specific filters for one user, or cross-org teams.
          !childOrgSelected ? (
            <SavedFilters
              showAgentList
              filterType={INBOX_FILTER_TYPE}
              filterFormOptions={['name', 'agents', 'tags', 'isDefault']}
              noFiltersTitle="Customize Live Listen"
              noFiltersMessage="Build your team by selecting individual agents or use tags that shows you only the agents you need."
              noFiltersButtonLabel="Create Team"
              selectedSavedFilterId={selectedSavedFilterId}
              handleSelectSavedFilter={handleSelectSavedFilter}
            />
          ) : null
        }
      />

      {!filtersLoaded || commandCenterLoading.agents ? (
        <Card className="no-padding">
          <div className="empty-table">
            <Loader centered />
          </div>
        </Card>
      ) : (
        <>
          <AgentCallStatusList />
          <Sentiment />
        </>
      )}
    </>
  )
}

export default withLDConsumer()(CommandCenterPage)
